/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { MonitorProcessData } from '@/types/models.ts';
import Tooltip from 'rc-tooltip';
import { classNames } from 'primereact/utils';
import { Conditional } from '@/components/Conditional.tsx';
import { DateTime, Interval } from 'luxon';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getLocaleTimeOffset, plurals, statusColors } from 'ogc-processes-components';
import { AppRoutes } from '@/constants/routes.ts';
import { NavLink } from 'react-router';
import { Utils } from '@/utils/utils.ts';
import { useJobsProcessInfoData } from './JobsMonitorContext';

interface Props {
  className?: string;
  monitorData: MonitorProcessData;
  size: number;
  spacing: number;
  maxPerLine: number;
}

const CRect = styled.rect`
  &:hover {
    stroke: var(--text-color);
    stroke-width: 3px;
    transition: stroke-width 0.2s;
  }
`;

export const JobsInfo: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation();
  const { monitorData, size, spacing, maxPerLine } = props;

  const rowCount = Math.ceil(monitorData.runs.length / maxPerLine);

  const { processesInfoData } = useJobsProcessInfoData();

  const rowProcessInfo = useMemo(() => {
    if (processesInfoData && monitorData) {
      const rowInfo = processesInfoData?.find(
        (el) => el.fecthStatus === 'sucessfull' && el.data?.process_id == monitorData.id
      );
      return rowInfo?.data;
    }
  }, [processesInfoData, monitorData]);

  const jobUrl = (jobId: string) => {
    let url = AppRoutes.jobs.detail.url(jobId);
    if (rowProcessInfo) {
      url += Utils.buildWithQueryParamsUrl({ job_reference: rowProcessInfo.project_name });
    }
    return url;
  };
  return (
    <svg
      width={maxPerLine * (size + spacing)}
      height={rowCount * (size + spacing)}
      className={classNames(props.className, 'mx-1')}
    >
      {monitorData.runs.map((jobInfo, index) => {
        const row = Math.floor(index / maxPerLine);
        const col = index % maxPerLine;
        const fillColor = statusColors[jobInfo.status];
        let duration = undefined;
        if (jobInfo.finished) {
          const created = DateTime.fromISO(jobInfo.created);
          const finished = DateTime.fromISO(jobInfo.finished);
          const interval = Interval.fromDateTimes(created, finished);
          duration =
            interval.length('seconds') <= 60
              ? `~ ${plurals(Math.ceil(interval.length('seconds')), 'sec')}.`
              : interval.length('minutes') <= 60
                ? `~ ${plurals(Math.ceil(interval.length('minutes')), 'min')}.`
                : ` ~ ${Math.ceil(interval.length('hours'))} h`;
        }

        const jobId = jobInfo.link.split('/').toReversed()[0];
        const [offsetMinute] = getLocaleTimeOffset();

        return (
          <Tooltip
            key={index}
            placement="bottom"
            overlay={
              <div className="">
                <div className="flex align-items-center">
                  <span className="inline-block font-bold">Id:&nbsp;</span>
                  <span className="">{jobId}</span>
                </div>
                <div className="flex align-items-center">
                  <span className="inline-block font-bold">Status:&nbsp;</span>
                  <span className="">{jobInfo.status}</span>
                </div>
                <Conditional condition={!!jobInfo.commit}>
                  <div className="flex align-items-center">
                    <span className="inline-block font-bold">Commit:&nbsp;</span>
                    <span className="">{jobInfo.commit}</span>
                  </div>
                </Conditional>
                <Conditional condition={!!jobInfo.reference}>
                  <div className="flex align-items-center">
                    <span className="inline-block font-bold">Reference:&nbsp;</span>
                    <span className="">{jobInfo.reference}</span>
                  </div>
                </Conditional>
                <div className="flex align-items-center">
                  <span className="inline-block font-bold">{t(['date.created_at', 'created'])}:&nbsp;</span>
                  <span className="">
                    {DateTime.fromISO(jobInfo.created)
                      .plus({ minute: offsetMinute })
                      .setLocale(i18n.language ?? 'en')
                      .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
                  </span>
                </div>
                {jobInfo.finished ? (
                  <div className="flex align-items-center">
                    <span className="inline-block font-bold">{t(['date.finished_at', 'finished'])}:&nbsp;</span>
                    <span className="">
                      {DateTime.fromISO(jobInfo.finished)
                        .plus({ minute: offsetMinute })
                        .setLocale(i18n.language ?? 'en')
                        .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
                    </span>
                  </div>
                ) : (
                  jobInfo.updated && (
                    <div className="flex align-items-center">
                      <span className="inline-block font-bold">{t(['date.updated_at', 'updated'])}:&nbsp;</span>
                      <span className="">
                        {DateTime.fromISO(jobInfo.updated)
                          .plus({ minute: offsetMinute })
                          .setLocale(i18n.language ?? 'en')
                          .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
                      </span>
                    </div>
                  )
                )}
                <Conditional condition={!!duration}>
                  <div className="flex align-items-center">
                    <span className="inline-block font-bold">{t(['misc.duration', 'duration'])}:&nbsp;</span>
                    <span className="">{duration}</span>
                  </div>
                </Conditional>
                <div className="flex align-items-center">
                  <span className="inline-block font-bold">{t(['misc.progress', 'progress'])}:&nbsp;</span>
                  <span className="">{jobInfo.progress} %</span>
                </div>
              </div>
            }
          >
            <NavLink to={jobUrl(jobId)}>
              <CRect
                className="cursor-pointer"
                key={index}
                width={size}
                height={size}
                x={col * (size + spacing) + 2}
                y={row * (size + spacing) + 2}
                fill={fillColor}
              />
            </NavLink>
          </Tooltip>
        );
      })}
    </svg>
  );
};
